<!--
  THE BANNER — one big thing at the top of a shelf, and then the next one.

  A hero is a promise that the artwork is worth the bytes, so this is the only
  place in the app allowed to ask for `background/medium` (1920×1080, a median
  379 KB). Everything else takes `background/small` at 20 KB — `thumbBackground()`.

  It draws the title's own LOGO when Cinemeta has one (98.3% of titles do; it is
  a PNG with alpha, 800×310 for four out of five) because a wordmark over a still
  is what makes this feel like a television rather than a database. Where there is
  none, the name is set in display type in the same box — the same box, so a logo
  that arrives late cannot push the caption down the screen.

  ── THE WHOLE BANNER IS THE BUTTON ──────────────────────────────────────────

  The owner stripped this surface to the picture and the caption: no kicker line,
  no Play/Details/Save row, no slideshow dots. What remains is the wordmark, the
  blurb, and the rating and year — and because every button is gone, THE BANNER
  ITSELF is the way in: one full-bleed link over the artwork opens the title
  page, with the title's name on it for the accessibility tree. Without that
  link the banner would be a dead end on a phone, which is exactly the space it
  was rebuilt for.

  CERTIFICATION: MEASURED, ABSENT. The brief asked for an age-certification chip
  beside the rating IF the data exists. It does not. Sampled 2026-08-16: 50
  movie catalogue rows, 50 series rows, and six /meta answers (movie and series,
  children's and adults' titles). The complete key inventory is: affiliates,
  awards, background, behaviorHints, cast, country, credits_cast, credits_crew,
  description, director, dvdRelease, genre, genres, id, imdbRating, imdb_id,
  links, logo, moviedb_id, name, popularities, popularity, poster, releaseInfo,
  released, runtime, slug, status, trailerStreams, trailers, tvdb_id, type,
  videos, writer, year. No certification, ageRating, mpaa or rated field, and no
  TV-14/PG-13-shaped value anywhere in the payloads — `awards` is prose, `links`
  is genres/cast/share. The only rating Cinemeta has is `imdbRating`, and it is
  drawn. A chip invented from data we do not hold would be a lie in a nice
  border — so the real source is now Wikidata (`certs.svelte.ts`, built by
  scripts/fetch-certifications.mjs; CC0, keyed by IMDb id): the chip draws
  beside the rating ONLY when that table actually answers, which is most films
  and honestly few series. It changes the meta line's width, never its height,
  so the reserved-box rule below still holds.

  ── IT IS A SLIDESHOW, AND THE RULES ARE THE POINT ───────────────────────────

  NOTHING MOVES BECAUSE AN IMAGE ARRIVED. Every backdrop is an absolutely
  positioned layer inside a box that is already the right size, and the box's
  height is viewport arithmetic (`--hero-h`), never content: the rails below
  cannot jump, because nothing the caption does is in their flow any more. The
  caption still reserves every row it owns — the wordmark box, the rating
  line, the blurb's lines — so a slide with a long synopsis and a slide with
  none draw the overlay in exactly the same place, and a logo that arrives
  late cannot nudge the meta line. The caption and the volume control are both
  absolutely positioned overlays: they take no layout space, so their coming
  and going cannot move a pixel of anything else — measured to zero CLS by
  construction, back when the old in-flow sound button had to reserve its slot
  to manage 0.0026.

  IT NEVER WAITS FOR A SLOW IMAGE. The timer advances on its own; a layer whose
  bytes have not landed shows the ground beneath and fades in when they do. The
  page is never held for a picture.

  IT LOADS ONE AT A TIME. Only the visible slide's `<img>` has a `src` at first
  paint. The next one is armed after `PRELOAD_AFTER_MS`, which is what "preload
  the next" has to mean when the thing being preloaded is 380 KB on a phone.

  IT STOPS WHEN YOU LOOK AT IT. Pointer over it, keyboard focus inside it (the
  banner link is a tab stop, so a keyboard can hold the slideshow by resting
  there), or the tab in the background — any one of those and it holds. And
  under `prefers-reduced-motion: reduce` it never advances by itself at all: it
  is a still banner, and still a link.

  THE SEQUENCE the owner asked for: the still holds for a beat, the trailer
  runs, and the slide ADVANCES when the trailer is done — on its `ENDED`, at
  the 30-second cap (`HERO_TRAILER_MAX_MS`: the cap is a page-turn, never a
  stop on a frozen frame), or after `SILENCE_MS` on a device that will not
  autoplay at all (Low Power Mode, blocked scripts). And around again: when
  the cycle comes back to a slide, its trailer plays AGAIN, from the
  beginning — `run` below is what guarantees the fresh dwell, and the Trailer
  is remounted with no `start`, so a revisit can never resume near its own end
  and instantly finish. Trailers always start MUTED; the round volume control
  (`MuteButton`, shared with the tiles) is overlaid bottom-right of the frame
  only once the still has actually been uncovered — a volume control
  over a still image is a lie — and the unmute happens synchronously inside
  its click, which is the only gesture WebKit honours.

  ── WHO GETS THE ONE TRAILER SLOT (the precedence rule) ──────────────────────

  One trailer plays app-wide (`trailer.svelte.ts`), and the arbiter is the
  app's single FOCUS: only the focused surface's dwell may claim the slot, and
  losing the focus forfeits it — this banner's own claim is torn down the
  moment `attended` flips (the `subject` key runs through the cap effect), so
  a tile the user is clearly attending is never starved by the banner. The
  banner cannot re-take the slot from a playing tile for the same reason: it
  is not focused while a tile is. `claimTrailer` stays last-writer-wins
  underneath, but by construction the only writer is whoever holds the
  attention. Measured in docs/TRAILERS.md-style probes (2026-08-17): pointer
  leaves the banner → the banner releases within one effect flush; the tile
  claims after its own dwell; the banner never claims again until it is
  attended again.

  ONE amendment on touch, where the focus is a guess rather than a fact:
  while the banner is MOSTLY ON THE GLASS and the focus is still only the
  ambient centre guess, the banner has PRIMACY — it counts as attended and
  tiles' automatic trailers stand down (`heroPrimary` in trailer-rules.ts;
  the measured defect is written on `primacy` below). A deliberate choice —
  a tap, an arrow key — beats primacy instantly, and scrolling the banner
  mostly away hands the screen to the tiles. So: chosen tile > banner >
  ambient-guessed tile, which is exactly "the hero should not starve tiles
  the user is clearly attending", read in both directions.

  ── THE ONE FRAME (2026-08-17, the third rebuild — and the owner's spec) ─────

  The maximised stage-plus-caption-band hero was QA'd on a screenshot and
  REJECTED: "the page only shows the hero … I don't want to see all this
  empty space at the bottom. I want this to all just be one frame — the
  graphical text of the title on the bottom left would be an overlay ON TOP
  of the trailer." So the split is gone. The hero is ONE FRAME: the still or
  the trailer IS the banner, and the caption — wordmark, meta line, blurb —
  is an OVERLAY on its bottom-left. No reserved caption band, no dead strip,
  and the frame, graphical text included, is the ENTIRE hero.

  ONE TOKEN OWNS THE SIZE: `--hero-h`, the frame's height — desktop
  clamp(420px, 62vh, 700px). The one-frame rebuild shipped at 56vh and the
  owner asked for "slightly larger" (QA 2026-08-17), so 62vh it is — measured
  at 1440×900: 558px of hero, the first rail's heading still clearly above
  the fold; the 700px ceiling is what lets a 1080p screen actually feel the
  bump (669px there) while a tall 4K portrait window still does not get a
  wall. The phone keeps min(84vw, 50vh) — the same QA sized the phone frame
  deliberately, the desktop bump is not a mandate to blow it back up, and at
  390×844 the 328px frame already reads full. Inside the frame the trailer's
  BOX is the largest uncropped 16:9 rect that fits (`--stage-h` — contain,
  centred on both axes), and the echo — the same blurred second embed,
  bar-crop factor and all — seamlessly fills whatever the frame's aspect
  leaves over: the sides on a wide short hero, the top-and-bottom slack on a
  narrow tall one. In the still phase the backdrop art cover-fills the whole
  frame, exactly as the banner always has.

  ── THE OVERSCAN — the video plays a little behind the frame (2026-08-17) ────

  The owner's rule: "let the trailer play a little bit larger than the
  actual frame, maybe 10 to 25 percent, but not past the maximum of cutting
  off the sides — we can cut off the top and bottom by a bit." So the stage
  is transform-scaled past exact fit: `--overscan` is
  min(1.15, heroWidth / stageWidth), measured off the frame's real box
  (`ResizeObserver` — 100vw would count the scrollbar and let the sides
  slip out by half its width). On a wide desktop the 1.15 binds (1440×900:
  the 992px stage plays at 1141px, 149px of surround each side, ~42px of
  picture cropped top and bottom by the frame's own clip); on any window
  where the stage already spans the width — every phone — the cap binds at
  exactly 1 and nothing is cropped, because the video's left and right edges
  must NEVER leave the frame. A transform, so YouTube still picks its
  quality rung from the stage's LAYOUT size and the zoom costs zero bytes —
  and the TV tier affords it for the same reason (compositor only). The
  bar-crop factor (`trailer-crop.ts`) COMPOSES with this safely by
  construction: the crop zooms the iframe INSIDE the stage's own clip, so it
  never widens the visible window — the sides cap is a bound on the stage
  box alone, and multiplying the two zooms can never push the window's edges
  past the frame.

  ── NO CONTAINED-THUMBNAIL BEAT — the backdrop IS the still (2026-08-17) ─────

  When the stage mounted it used to draw the Trailer's own still — the same
  backdrop, but CONTAINED in the 16:9 box over the cover-filled copy behind
  it: a visible zoom-out to the full letterboxed thumbnail for the whole
  pre-reveal beat. Owner: "I don't want to see that transition of the
  thumbnail — I just want the video to start." So the stage now mounts
  INVISIBLE (opacity 0, and `noStill` on the Trailer — nothing inside it can
  bleed through a cross-fade), the cover backdrop holds unchanged while the
  embed loads and YouTube's branding clears, and on the reveal verdict
  (`uncovered` — the same clock as everything else) the stage and the dim
  fade in as the sharp backdrop steps down: one straight fade from the
  zoomed still to the moving video, nothing letterboxed in between. The
  refusal branch keeps its answer: a device that never plays never fires the
  verdict, so the backdrop simply stays until the silence timer turns the
  page — the still phase was never taken away. And the end screen never
  shows because ENDED advances the slideshow in the same tick, unmounting
  the stage.

  THE OVERLAY QUIETENS DURING PLAYBACK — the owner's rule: "if the trailer is
  playing, make them a bit more transparent so they don't block anything —
  still there, just very transparent and light, not as bold." Still phase:
  the caption at full presence over the low scrim. The moment the video is
  uncovered — the SAME reveal-verdict clock the echo and the sharp-art
  step-down ride (`uncovered`) — the caption drops to its light register:
  wordmark to 0.62, meta and blurb to 0.42, opacity ONLY (the one transition
  the TV tier snaps for free), 700ms on `--ease` like the layers swapping
  around it. Clearly there if you look, never blocking the picture. Any way
  back to a still — the advance, an error, a swipe — restores full presence,
  because `stopTrailer` clears `uncovered`; the scrim thins at the same
  moment (`.art.playing .scrim`) so the footage never reads as dimmed.

  ── THE ECHO — the whole screen is the trailer, on every device ──────────────

  The owner's rule (2026-08-17): "the background that isn't playing [should
  be] the same trailer playing but blurred, edge-to-edge, so it gives the
  sensation of the whole screen being used to play the trailer" — and a
  static backdrop must NEVER sit behind a playing trailer, on ANY device. A
  cross-origin iframe's pixels cannot be captured, so the echo is a SECOND
  muted, chrome-free, aria-hidden, pointer-inert embed of the same video,
  started together with the stage's (no sync machinery — under the heavy blur
  the drift is invisible), laid out at a fixed 384×216 and transform-scaled
  over the hero so the bytes stay on the cheap quality rung and the blur
  filter runs on a small surface (the measured story is on the frame-creation
  code below). It runs on phone, tablet, desktop AND television; the TV tier
  keeps the same treatment because it was measured affordable at 6×/8× CPU
  throttle — the filter rasterises 384×216, not the screen (numbers on the
  `.echo` CSS). It wears the stage's bar-crop factor (`trailer-crop.ts`) so
  the two layers agree about where the picture is.

  The swap rides ONE clock: the echo fades in on the SAME reveal verdict as
  the stage (`uncovered`) once it has itself reported PLAYING, and at that
  same instant the backdrop's sharp layer steps down (`.art.playing`) so no
  static artwork ever sits behind moving video — what remains beneath is the
  ambient blur, which is the "blurred still" surround for the moments before
  the echo lands and for every failure. Reduced motion and Save-Data never
  reach any of this (the trailer itself is refused — that is a consent path,
  not a device split). The echo never claims the trailer slot, never unmutes,
  and dies with the slide. It is a deliberate, measured exception to
  docs/TRAILERS.md § F4's one-embed rule.

  ── THE MELT — the banner dissolves into the page ────────────────────────────

  The hero's bottom edge used to meet the content with a visible seam (worst
  on iPhone/iPad): the scrim faded to flat `--bg` while the page ground is
  `--app-ground` — a warm WASH over `--bg` — so the two darks did not match,
  and during a trailer the dim and the echo ended in a hard line. The fix is a
  mask, not more gradient: `.art` (the whole stack — backdrop, dim, echo,
  scrim, stage) fades to transparent over its lowest `--hero-melt`, so
  whatever the hero is showing dissolves into the page's own ground, still
  phase and trailer phase alike. A mask costs a compositor multiply, no
  filter, so the TV tier affords it. In the one-frame hero the stage reaches
  the frame's bottom edge, so the melt dissolves the picture's own lowest
  pixels — deliberate: with no band beneath to hide the join, the melt IS how
  the frame ends without a seam, and it is kept shallower than the band-era
  value so it reads as a dissolve rather than a missing slice of video.

  ── VOLUME TOUCHED = UNMUTE THIS TRAILER ─────────────────────────────────────

  Reaching for the volume while a trailer plays means "I want to hear this"
  (`volume-intent.ts` — the TV shell forwards its volume keys as
  `jt:volumekey`; some desktop browsers deliver AudioVolume keys). While THIS
  banner holds the trailer claim and the video is actually showing, the
  intent unmutes the stage's player synchronously in the callback — the same
  one-trailer-only semantics as the MuteButton, whose icon follows because
  `heard` is the shared state. The next trailer starts muted as always
  (`stopTrailer` resets `heard`), and the echo can never make noise: the
  intent goes only to the stage's handle.

  ── A FINGER TURNS THE PAGE ──────────────────────────────────────────────────

  Swipe left for the next slide, swipe right for the one before — the same
  dealt order walked backwards, wrapping at the ends. The verdict is given at
  pointerup: at least 40px of horizontal travel, and not markedly MORE vertical
  than horizontal. The dominance bar is deliberately generous (owner QA
  2026-08-17: "it has to be a perfect swipe — I don't want it to have to be
  perfect"): `touch-action: pan-y` already hands clearly-vertical gestures to
  the browser as scrolls (which fire pointercancel and end our tracking), so
  any gesture that survives to pointerup has beaten the pan detector and a
  diagonal-ish drag with real horizontal travel is a swipe. What is left out is
  a tap (the link's business, under 40px) and the near-vertical remainder. A
  swipe that lands does exactly what the automatic advance does — tears the
  trailer down, hands the new slide a fresh still-then-trailer clock — and
  swallows the click the browser fires after the pointerup, because a swipe
  must never double as "open the title". Touch and pen only: a mouse drags
  links, and a television has no fingers.

  ── THE DOTS — the slideshow admits it is one (owner spec 2026-08-17) ────────

  The one-frame redesign removed the slide dots; the owner asked for them
  back in a new form: "very small dots centred under the hero … The dots
  become pills for the one that is currently active, and the pill has a
  small animated loading bar that shows the progress of the trailer." So: a
  centred row of 5px dots pinned to the frame's last pixels — INSIDE the
  melt, where the art has already dissolved to the page's own dark ground
  and the low scrim is at its strongest, which is what lets them read over
  bright artwork with no scrim of their own (each pip wears only a 1px dark
  halo for the worst case). They are an overlay like everything else here:
  absolutely positioned, zero layout, nothing moves when they mount.

  The active dot stretches into a 30px pill (width only, `--ease-out`), and
  the pill holds a `--accent` fill — progress is pink in this app; the pips
  themselves stay neutral text-mixes, because the inactive dots are not the
  brand. THE FILL IS THE TRAILER'S PROGRESS, literally: it stays EMPTY
  through the still phase — deliberate, the owner's words were "shows the
  progress of the trailer", and the still hold is not a fixed beat (the
  dwell is an attention-gated stopwatch that pauses on scroll, and the
  no-trailer fallback holds for `intervalMs`), so a bar animating over an
  "expected" hold would routinely lie. It fills toward the ADVANCE POINT,
  whichever clock wins: max(wall-clock progress toward the 30s cap,
  playback progress toward the known duration) — the cap timer runs on wall
  clock from the stage opening, so this is the only formula that completes
  exactly when the page actually turns; duration unknown means the cap leg
  alone. Driven by a 4Hz poll writing an inline scaleX — JS state, not a
  CSS transition — so it still MOVES on TV, where every transition snaps to
  0.01ms; the 260ms linear transition on top only smooths the steps where
  transitions exist. Under reduced motion it updates discretely, which that
  preference is fine with.

  A dot press JUMPS to that slide through the same teardown-and-move the
  swipe uses (stop the trailer, move the index; the dwell keys on `subject`,
  so the landing slide gets its fresh still-then-trailer clock), and it
  stops propagation like the MuteButton so a press can never double as
  "open the title". On TV the row is informational: `data-dpad-skip` keeps
  it out of the remote's map (the banner stays ONE stop) and `tabindex=-1`
  keeps a stray TAB off it; it stays visible, because position is worth
  knowing on a sofa too.

  ── THE ECHO, BRIGHTER AND IN LOCKSTEP (owner QA 2026-08-17, second pass) ────

  "The blurred version of the trailer is too dim. I want it brighter. Also
  it's just slightly behind … It should be perfectly in sync. Try not to
  waste internet by downloading it twice — we can probably just use the same
  video stream."

  BRIGHTER: the echo's brightness leg rises 0.55 → 0.8 (the TV tier's alpha
  quad, which does the same job without a filter, thins 0.52 → 0.32). The
  caption keeps its floor because the low scrim and the melt still own the
  bottom of the frame, and YouTube's chrome stays illegible because
  illegibility never came from the dimming — it is blur(16px) × the ~3–5×
  scale (bilinear mush × 1.5 oversize on TV) that destroys it.

  IN LOCKSTEP: the two embeds used to merely start together (~0.05–0.08s
  apart, and the owner saw it at hard cuts). Now a corrector polls both
  players' clocks — 1Hz on watch, 4Hz only while correcting — and when they
  disagree by more than 0.06s it runs the ECHO at 1.25×/0.75× until they
  agree within 0.04s: YouTube's player only has quarter-step rates (see
  `rate` in yt.ts), so the "gentle nudge" is a quarter step, invisible under
  the blur, and convergence at 4Hz lands within about a frame. A drift past
  1s — a stall, a late start — is the one case that seeks instead, because
  a seek rebuffers and a rebuffer under blur is a visible freeze. The
  corrector lives and dies inside the echo's own effect, touches ONLY the
  echo (the stage player is the clock, never the follower), and a player
  that is not PLAYING is left alone.

  THE SAME STREAM TWICE IS NOT A CHOICE WE HAVE — the owner asked to reuse
  one stream, and it cannot be done: a cross-origin iframe's pixels cannot
  be captured (no drawImage, no captureStream, nothing for CSS to mirror —
  an iframe is not an <img>), so the only way to put the footage in two
  places is two embeds. The honest mitigation is already here: the echo
  lays out at 384×216, so YouTube serves it the cheap rung — the surround
  costs a fraction of the stage's stream (both sides measured in this
  branch's verification notes).
-->
<script lang="ts">
  import type { CatalogItem } from '$types';
  import CertChip from './CertChip.svelte';
  import Icon from './Icon.svelte';
  import MuteButton from './MuteButton.svelte';
  import Skeleton from './Skeleton.svelte';
  import Trailer from './Trailer.svelte';
  import { certOf } from './certs.svelte';
  import { backgroundUrl } from './cinemeta';
  import { claimTopGround } from './top-ground.svelte';
  import { beginDwell, dwellLeft, haltDwell, runDwell } from './dwell';
  import { focusState, focusable, isFocused, newFocusId } from './focus.svelte';
  import { titleHref } from './nav.svelte';
  import {
    HERO_DWELL_MS,
    HERO_TRAILER_MAX_MS,
    SILENCE_MS,
    STILL_HOLD_MS,
    autoplayAllowed,
    claimTrailer,
    holdsTrailer,
    releaseTrailer,
    revealIn,
    revealVerdict,
    setHeroPrimacy,
    spendAutoTrailer,
    watchConditions
  } from './trailer.svelte';
  import { cropFactorFor } from './trailer-crop';
  import { onVolumeIntent } from './volume-intent';
  import { PLAYING, attachYt, type YtHandle } from './yt';

  interface Props {
    /** The slides, in order. One is a still banner; none is a skeleton. */
    items: CatalogItem[];
    /** How long each slide holds. Slow on purpose: this is furniture, not an ad. */
    intervalMs?: number;
  }

  let { items, intervalMs = 8000 }: Props = $props();

  /**
   * Five, not fifty. Each slide is a 380 KB backdrop the moment it is reached,
   * and a sixth one nobody waits around for is 380 KB of somebody's data.
   */
  const MAX_SLIDES = 5;
  /**
   * How long a slide is on screen before the NEXT one is fetched.
   *
   * Not "as soon as this one has loaded", which is what it was: measured, the
   * first backdrop landed at 585 ms and the second one — 724 KB of it — started
   * immediately, competing with the first screenful of posters for a phone's
   * connection. The slide holds for eight seconds, so three is late enough to
   * stay out of the way of first paint and early enough to have five seconds of
   * runway. If it is still not there when the timer fires, the layer simply
   * fades in when it arrives; nothing waits.
   */
  const PRELOAD_AFTER_MS = 3000;

  const slides = $derived(items.slice(0, MAX_SLIDES));
  const signature = $derived(slides.map((s) => s.id).join(','));

  let index = $state(0);
  /**
   * Which slides have been given a `src`.
   *
   * TWO COPIES ON PURPOSE, and they have different jobs. `armedSeen` is the
   * guard and is deliberately NOT reactive — `arm()` is called from an effect,
   * and an effect that reads the very thing it writes re-runs itself. `armed` is
   * the mirror the template reads, written whole so the change is unmissable.
   */
  let armedSeen = new Set<number>([0]);
  let armed = $state<boolean[]>([true]);
  let hovering = $state(false);
  let focused = $state(false);
  let backgrounded = $state(false);
  let reduced = $state(false);
  let onscreen = $state(true);
  let node = $state<HTMLElement | null>(null);

  /** Per-slide artwork verdicts, so one missing wordmark does not blank the rest. */
  let logoFailed = $state<boolean[]>([]);
  let artFailed = $state<boolean[]>([]);

  const current = $derived(slides[index] ?? null);

  /* ── A new shelf is a new slideshow ─────────────────────────────────────── */

  let lastSignature = $state('');
  $effect(() => {
    const now = signature;
    if (now === lastSignature) return;
    lastSignature = now;
    index = 0;
    armedSeen = new Set<number>([0]);
    armed = [true];
    logoFailed = [];
    artFailed = [];
  });

  /**
   * DATA SAVER IS THE SAME ANSWER AS REDUCED MOTION.
   *
   * Every turn of the banner is a backdrop and a wordmark it did not have —
   * measured on this shelf at roughly 0.5–1.2 MB a slide. Somebody who has told
   * their phone to spend less has told us too: the banner stops on the one they
   * were given. Read once, because the flag does not change while a page is open.
   */
  const thrifty =
    typeof navigator !== 'undefined' &&
    (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;

  /* ── Motion preference, read once and then followed ─────────────────────── */

  $effect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = (): void => {
      reduced = media.matches;
    };
    apply();
    if (typeof media.addEventListener === 'function') media.addEventListener('change', apply);
    else media.addListener(apply);
    return () => {
      if (typeof media.removeEventListener === 'function') media.removeEventListener('change', apply);
      else media.removeListener(apply);
    };
  });

  /* ── A backgrounded tab is not an audience ──────────────────────────────── */

  $effect(() => {
    if (typeof document === 'undefined') return;
    const apply = (): void => {
      backgrounded = document.visibilityState === 'hidden';
    };
    apply();
    document.addEventListener('visibilitychange', apply);
    return () => document.removeEventListener('visibilitychange', apply);
  });

  /*
   * A BANNER NOBODY CAN SEE IS NOT A BANNER, IT IS A DOWNLOAD.
   *
   * Each slide is a ~380 KB backdrop plus an ~80–320 KB wordmark, fetched the
   * first time it is reached. Measured on the shelf: scrolling Home to the
   * bottom while the slideshow ran off the top of the screen pulled about 1.8 MB
   * of pictures nobody was looking at. Off screen it stops, and stops arming.
   */
  /** True while at least 60% of the banner is on the glass — the primacy bar. */
  let mostlyVisible = $state(false);
  $effect(() => {
    const el = node;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          onscreen = e.isIntersecting;
          mostlyVisible = e.isIntersecting && e.intersectionRatio >= 0.6;
        }
      },
      // 0 keeps the old any-pixel `onscreen` answer; the band around 0.6 keeps
      // `mostlyVisible` honest without a callback per scrolled pixel.
      { threshold: [0, 0.55, 0.6, 0.65] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  });

  /* ── The trailer, when the banner has held somebody's attention ─────────── */

  const uid = newFocusId('hero');
  const trailerId = $derived(current?.trailers?.[0] ?? null);
  const alternates = $derived(current?.trailers?.slice(1) ?? []);

  /**
   * CENTRE-GUESS MODE, read once like `focus.svelte.ts` reads it: no real
   * pointer, so the focus is inferred from what is centred. This is the only
   * mode in which the banner asserts primacy — under a pointer, hover already
   * says where the attention is.
   */
  const centreGuess =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    !window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /**
   * PAGE-TOP PRIMACY — the fix for the measured page-top defect (2026-08-17,
   * 390x844): the ambient focus is "the row nearest the middle", and with the
   * banner this short, that guess lands on the FIRST
   * RAIL'S FIRST TILE the moment Home opens — the tile took the one trailer
   * slot at 1.9 s and the banner never played at all. While the banner is
   * mostly on the glass and the focus is still just the centre guess, the
   * banner is the presumed attention and runs its channel; a deliberate
   * choice (a tap's `pointer` write, a remote's `keyboard` write — anything
   * that is not the ambient 'centre' source) takes it away instantly, and so
   * does scrolling the banner mostly off. Published to `trailer.svelte.ts`,
   * where the tiles' `autoplayAllowed` respects it.
   */
  const primacy = $derived(
    centreGuess &&
      mostlyVisible &&
      trailerId !== null &&
      (focusState.id === null || focusState.id === uid || focusState.source === 'centre')
  );
  $effect(() => {
    setHeroPrimacy(primacy);
    return () => setHeroPrimacy(false);
  });

  /** Attended: the one app focus, or the page-top primacy standing in for it. */
  const attended = $derived(isFocused(uid) || primacy);

  watchConditions();

  let trailerOn = $state(false);
  /** The live embed, once it has attached. Sound goes through it. */
  let player = $state<YtHandle | null>(null);
  /** Set by a press, and only by a press: unmuting needs a real gesture. */
  let heard = $state(false);

  /**
   * When the stage opened — the clock the reveal mirror below runs on. Plain on
   * purpose, like `bannerSpent`: it is written in the same timeout that flips
   * `trailerOn` and only ever read alongside `playingAt`, whose change re-runs
   * the effect that wants it.
   */
  let stageAt = 0;
  /** When the embed first reported PLAYING, via the Trailer's `onstate`. */
  let playingAt = $state<number | null>(null);
  /**
   * OUR MIRROR OF THE TRAILER'S REVEAL — true once the still has actually been
   * uncovered. The Trailer keeps its reveal private and is shared with other
   * surfaces, so instead of reaching into it this runs the SAME pure rule
   * (`revealVerdict` from trailer-rules) on the same inputs. It is what lets
   * the volume control appear at the moment the video does and never a frame
   * over the still.
   */
  let uncovered = $state(false);

  /**
   * WHICH PLAY THIS IS. Bumped every time a trailer stops, so the `subject`
   * below changes even when nothing else did — which is what hands a slide a
   * FRESH dwell (a fresh two-second still beat) before its trailer plays
   * again. Without it a single-slide banner, whose index and id never change,
   * would re-claim the instant its trailer ended, with no beat at all; and a
   * cycled-back slide inherits the guarantee for free.
   */
  let run = $state(0);

  /**
   * WHAT THIS BANNER IS OFFERING RIGHT NOW. Changing slide is leaving the old
   * title however long the pointer has been sitting there, and a shelf being
   * replaced underneath is the same thing again.
   */
  const subject = $derived(`${attended}|${trailerId ?? ''}|${index}|${run}|${signature}`);

  /*
   * THE DWELL IS A STOPWATCH, NOT A COUNTDOWN — see `dwell.ts`. It runs while
   * the banner is attended and the screen is still, and it PAUSES rather than
   * restarts while the page is moving. That one change is the iPhone fix: the
   * focus used to be cleared by every `scroll` and every `resize`, so a thumb
   * nudging the page 18 px every two seconds pushed the trailer from 6 s to
   * never (docs/TRAILERS.md § C2), and Safari's collapsing URL bar fires
   * `resize` on the first scroll of every visit.
   */
  let dwell = beginDwell(HERO_DWELL_MS);
  let dwellSubject = '';

  $effect(() => {
    const key = subject;
    const moving = focusState.moving;
    const may = attended && trailerId !== null && autoplayAllowed(true, 'hero');
    if (key !== dwellSubject) {
      dwellSubject = key;
      dwell = beginDwell(HERO_DWELL_MS);
    }
    if (!may || trailerOn || moving) {
      dwell = haltDwell(dwell, Date.now());
      return;
    }
    const id = trailerId;
    dwell = runDwell(dwell, Date.now());
    const start = setTimeout(() => {
      if (id === null) return;
      claimTrailer(uid, id);
      spendAutoTrailer();
      stageAt = Date.now();
      playingAt = null;
      uncovered = false;
      trailerOn = true;
    }, dwellLeft(dwell, Date.now()));
    return () => {
      clearTimeout(start);
      dwell = haltDwell(dwell, Date.now());
    };
  });

  function stopTrailer(): void {
    // A play that actually happened moves the banner to its next `run`, which
    // is what re-arms the dwell — see the `run` note above.
    if (trailerOn) run += 1;
    trailerOn = false;
    heard = false;
    playingAt = null;
    uncovered = false;
    releaseTrailer(uid);
  }

  /*
   * THE TRAILER IS WHAT ADVANCES THE BANNER.
   *
   * The still used to hold for a fixed interval whether or not anything played,
   * so a slide could finish its trailer and then sit on a frozen frame for the
   * remainder of its turn. The sequence the owner asked for is: hold the picture
   * for a beat, run the trailer, move on when it is done — and when the last one
   * has run, start again from the top rather than stopping, every slide playing
   * its trailer again from the beginning on every pass.
   */
  /**
   * ONE STEP, EITHER WAY — the teardown-and-move that the trailer's ending and
   * a finger's swipe share, so a manual change can never leave playing state
   * behind that the automatic one would have cleaned up. Backwards is the same
   * dealt order read the other way; both directions wrap.
   */
  function slideBy(delta: 1 | -1): void {
    stopTrailer();
    if (slides.length > 1) index = (index + delta + slides.length) % slides.length;
  }

  function trailerFinished(): void {
    slideBy(1);
  }

  /*
   * The cap, and the release. Separate from the dwell on purpose: the dwell's
   * effect re-runs every time the page starts or stops moving, and a trailer
   * that is already playing must not be torn down by a scroll.
   *
   * THE SILENT DEVICE GETS ITS SLIDESHOW BACK. An iPhone in Low Power Mode (or
   * any device that refuses muted autoplay) never reports PLAYING; the old
   * banner offered a tap-to-play for that case, but a tap on this banner now
   * means "open the title", so the offer is gone — and without this timer such
   * a device would sit the full thirty-second cap on a frozen frame, five
   * slides in a row. `SILENCE_MS` is the measured "it is never going to play
   * here" verdict (PLAYING lands at a median 781 ms); the extra grace covers a
   * slow start, and `playingAt` is read inside the timer, not the effect, so a
   * late PLAYING cannot re-arm anything.
   */
  $effect(() => {
    void subject;
    if (!trailerOn) return;
    const cap = setTimeout(trailerFinished, HERO_TRAILER_MAX_MS);
    const silence = setTimeout(() => {
      if (playingAt === null) trailerFinished();
    }, SILENCE_MS + 1500);
    return () => {
      clearTimeout(cap);
      clearTimeout(silence);
      stopTrailer();
    };
  });

  /* ── The overscan — the stage plays a little larger than exact fit ──────── */

  /**
   * THE TARGET, from the owner's 10–25% band. 1.15 is the low-middle of it on
   * purpose: the bar-crop can add its own ≤15% zoom inside the stage, and the
   * melt eats the frame's lowest pixels, so a higher target starts costing
   * faces near the top of frame for footage that is already zoomed.
   */
  const OVERSCAN = 1.15;

  /**
   * The frame's REAL box, not 100vw: the viewport unit counts the scrollbar,
   * and a cap computed from it would let the video's sides slip past the
   * frame by half a scrollbar. A ResizeObserver instead of a resize listener
   * because the hero's height is viewport arithmetic — `--hero-h` moves with
   * vh — and the observer answers both dimensions in one callback with no
   * layout read of our own.
   */
  let heroW = $state(0);
  let heroH = $state(0);
  $effect(() => {
    const el = node;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        heroW = entry.contentRect.width;
        heroH = entry.contentRect.height;
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  });

  /**
   * min(1.15, heroWidth / stageWidth) — the sides cap. The stage's contain
   * maths is redone here in numbers (height-capped on wide frames,
   * width-capped on narrow ones) so the cap divides by the same width the
   * CSS lays out. Where the stage already spans the frame — every phone —
   * the quotient is exactly 1 and the video's edges stay on the frame's:
   * sides are NEVER cropped, only the 1.15 leg ever crops, and it crops top
   * and bottom alone via the frame's `overflow: clip`.
   */
  const overscan = $derived.by(() => {
    if (!(heroW > 0) || !(heroH > 0)) return 1;
    const stageH = Math.min((heroW * 9) / 16, heroH);
    const stageW = (stageH * 16) / 9;
    return Math.min(OVERSCAN, heroW / stageW);
  });

  const trailerPlaying = $derived(trailerOn && trailerId !== null && holdsTrailer(uid));
  /** The volume is real only over a moving picture: a player, and the still gone. */
  const showVolume = $derived(trailerPlaying && player !== null && uncovered);

  /** The Trailer's every state change; the first PLAYING starts the reveal clock. */
  function noteState(state: number): void {
    if (state === PLAYING && playingAt === null) playingAt = Date.now();
  }

  /* The reveal mirror. One timer, armed exactly once per PLAYING, not a poll. */
  $effect(() => {
    const at = playingAt;
    if (!trailerOn || at === null) return;
    const input = { openedAt: stageAt, playingAt: at, errored: false, holdMs: STILL_HOLD_MS };
    const settle = (): void => {
      if (revealVerdict(input, Date.now()) === 'show') uncovered = true;
    };
    settle();
    const wait = revealIn(input, Date.now());
    if (!Number.isFinite(wait)) return;
    const timer = setTimeout(settle, wait + 16);
    return () => clearTimeout(timer);
  });

  /* ── The echo — see the header. A second, blurred, always-muted embed. ───── */

  const embedOrigin = typeof location === 'undefined' ? '' : location.origin;

  /**
   * EVERY SURFACE, phone to television — the owner rejected the desktop-only
   * split (2026-08-17): the sensation is "the whole screen plays the
   * trailer", and a phone's surround or a TV's side bleed deserve it the
   * same as a desktop's. `reduced`/`thrifty` are not device splits: under
   * either the trailer itself never autoplays, so this guard is only against
   * the deliberate-press path ever growing an echo on a consent path.
   */
  const echoWanted = $derived(trailerPlaying && !reduced && !thrifty);

  /**
   * TV runs the echo too, but UNFILTERED — the treatment differs, not the
   * presence. Measured (headless Chromium, software raster, 1280×720, echo
   * live, 5 s rAF windows): at 6× CPU throttle the page idles at 55.8 fps
   * with no echo, 5.6 fps with the blur(16px) filter, 8 fps at blur(8px),
   * and 17 fps even for a bare brightness+saturate — ANY filter chain on a
   * per-video-frame surface is the cost, not the radius. So on TV the echo
   * wears no filter at all: the 384×216 frame stretched ~5× is its own blur
   * (bilinear mush), a plain alpha overlay quad does the dimming (`.echo`'s
   * TV ::after — compositing, not filtering), and the oversize below rises
   * to 1.5 so YouTube's title bar and wordmark corner fall OUTSIDE the hero
   * instead of relying on blur for illegibility.
   */
  const tvOn =
    typeof document !== 'undefined' && document.documentElement.dataset.tv === 'on';
  const ECHO_OVERSIZE = tvOn ? 1.5 : 1.15;

  let echoHole = $state<HTMLDivElement | null>(null);
  /** The echo's own PLAYING — it fades in only once there is real motion in it. */
  let echoLive = $state(false);

  /**
   * The stage's URL parameters minus the ones that are about being watched:
   * always muted, never a start offset (it begins with the stage's embed and
   * drifts imperceptibly under the blur), captions moot behind 52px of blur but
   * silenced anyway via `attachYt`.
   */
  function echoUrl(id: string): string {
    const p = new URLSearchParams({
      autoplay: '1',
      mute: '1',
      playsinline: '1',
      controls: '0',
      rel: '0',
      iv_load_policy: '3',
      fs: '0',
      disablekb: '1',
      loop: '0',
      cc_load_policy: '0',
      enablejsapi: '1'
    });
    if (embedOrigin) p.set('origin', embedOrigin);
    return `https://www.youtube-nocookie.com/embed/${id}?${p.toString()}`;
  }

  /*
   * Built and torn down exactly like the Trailer's frame (see that component's
   * header for why the iframe is made in JS), by the one effect that owns it.
   * It NEVER touches `claimTrailer` — it is scenery, not the app's playing
   * trailer — and `askForSound` never reaches it, so it cannot make noise.
   */
  $effect(() => {
    const host = echoHole;
    const id = echoWanted ? trailerId : null;
    if (!host || id === null) return;

    const frame = document.createElement('iframe');
    frame.src = echoUrl(id);
    frame.title = '';
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('frameborder', '0');
    frame.setAttribute('loading', 'eager');
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    frame.allow = 'autoplay; encrypted-media';
    frame.tabIndex = -1;
    /*
     * LAID OUT SMALL, SCALED UP LARGE — both the bytes and the jank hang on
     * this one move. YouTube picks its quality rung from the iframe's LAYOUT
     * size (docs/TRAILERS.md § D3), so a surround-sized frame was served
     * 720p+: measured 2026-08-17 at 1440x900, the echo took the hero's 40 s
     * YouTube bill from 7.1 MB to 17.0 MB — pixels the blur then destroyed.
     * And the blur itself ran over a ~2160px-wide surface every scrolled
     * frame: +25% frame time at 6x CPU throttle. So the frame is a fixed
     * 384x216 — the same rung a poster tile gets — the blur is applied to
     * THAT small surface (see `.echo` CSS), and a single transform scales the
     * already-blurred picture up over the hero: the effective softness is
     * blurpx x scale, and the filter cost stays 384x216. The scale covers the
     * hero with margin whichever of its edges is the long one; resize
     * mid-slide is not tracked — the next slide's echo reads the new size.
     */
    const box = host.getBoundingClientRect();
    const scale = (Math.max(box.width, (box.height * 16) / 9) * ECHO_OVERSIZE) / 384 || 4;
    frame.style.transform = `translate(-50%, -50%) scale(${scale.toFixed(3)})`;
    host.replaceChildren(frame);

    let gone = false;
    let handle: YtHandle | null = null;

    /*
     * The stage's bar-crop factor, multiplied in, SO THE LAYERS AGREE: a
     * letterboxed trailer's bars would otherwise smear two dark bands across
     * the surround while the stage showed none. Cached, so this resolves from
     * memory on every slide after the first look at a video.
     */
    void cropFactorFor(id).then((factor) => {
      if (gone || factor === 1) return;
      frame.style.transform = `translate(-50%, -50%) scale(${(scale * factor).toFixed(3)})`;
    });
    void attachYt(frame, {
      onplaying: () => {
        if (!gone) echoLive = true;
      }
    })
      .then((h) => {
        if (gone) {
          h.destroy();
          return;
        }
        // Belt and braces on top of mute=1: the echo must never be audible.
        h.mute();
        handle = h;
      })
      .catch(() => {
        // The API script is blocked; the surround keeps the blurred still.
      });

    /*
     * THE SYNC CORRECTOR (header note). The stage's clock is the truth and
     * the echo is the follower — this NEVER touches the main player. 1Hz on
     * watch (every fourth beat), 4Hz only while a nudge is running, and both
     * `time()` reads are the API's cached clock, so the steady cost is two
     * property reads a second — the TV tier does not feel it. Drift past
     * 0.06s starts a quarter-step rate nudge (all YouTube offers — see
     * `rate` in yt.ts) that runs until the clocks agree within 0.04s; at 4Hz
     * each beat moves the drift 0.0625s, so convergence lands within about a
     * frame and cannot oscillate past the threshold. Only a drift past 1s —
     * a stall, a very late start — seeks, because a seek rebuffers and a
     * rebuffer under the blur is a visible freeze. `player` is read inside
     * the callback (untracked), so the echo is not rebuilt when the stage's
     * handle attaches.
     */
    let nudging: 0 | 1 | -1 = 0;
    let beat = 0;
    const corrector = setInterval(() => {
      beat = (beat + 1) % 4;
      if (nudging === 0 && beat !== 0) return;
      const main = player;
      const echo = handle;
      if (main === null || echo === null) return;
      if (main.state() !== PLAYING || echo.state() !== PLAYING) return;
      const drift = main.time() - echo.time();
      if (import.meta.env.DEV) console.debug(`[echo-sync] drift ${drift.toFixed(3)}s`);
      if (Math.abs(drift) > 1) {
        // A scene away: one jump, with a small lead to cover the seek's own
        // latency, and any running nudge is cancelled with it.
        echo.seek(main.time() + 0.1);
        if (nudging !== 0) {
          echo.rate(1);
          nudging = 0;
        }
        return;
      }
      if (nudging !== 0) {
        // `drift * nudging` falls through 0.04 exactly when the gap the
        // nudge was chasing has closed, whichever sign it started with.
        if (drift * nudging <= 0.04) {
          echo.rate(1);
          nudging = 0;
        }
        return;
      }
      /*
       * 0.06, not the first pass's 0.12: with the looser bar the drift was
       * measured settling at a rock-steady +0.062s — a frame and a half at
       * 24fps, which is precisely the offset the owner said he could see at
       * hard cuts. At 0.06 one quarter-rate beat (~0.0625s of correction)
       * closes the residual to ~0, and the settle bar at 0.04 keeps the
       * pair from ping-ponging across zero.
       */
      if (Math.abs(drift) > 0.06) {
        nudging = drift > 0 ? 1 : -1;
        echo.rate(drift > 0 ? 1.25 : 0.75);
      }
    }, 250);

    return () => {
      gone = true;
      clearInterval(corrector);
      echoLive = false;
      handle?.destroy();
      host.replaceChildren();
    };
  });

  /**
   * ASKING FOR SOUND DOES NOT RELOAD ANYTHING. It used to rebuild the embed with
   * `mute=0` and `start=<elapsed>`, which is a second load — and now that the
   * still is held over the frame until YouTube's branding has gone, a reload
   * would put three seconds of YouTube title bar back on screen. `unmute()`
   * from inside this click handler is a real gesture, which is the only thing
   * that may unmute, and the picture never flinches.
   */
  function askForSound(): void {
    if (!player) return;
    if (heard) {
      player.mute();
      heard = false;
    } else {
      player.unmute();
      heard = true;
    }
  }

  /*
   * VOLUME TOUCHED = UNMUTE THIS TRAILER (header note). Subscribed for the
   * banner's whole life — the callback itself checks whether the banner is
   * the playing surface, because the subscription must not churn on every
   * claim. The unmute happens SYNCHRONOUSLY in the callback: where the
   * intent came from a real key event this is inside its gesture window,
   * which is the only unmute WebKit honours. `heard = true` flips the
   * MuteButton with it; a volume touch never mutes — reaching for the rocker
   * means "louder or quieter", and both mean "I want to hear it".
   */
  $effect(() => {
    return onVolumeIntent(() => {
      if (!trailerPlaying || !uncovered || player === null || heard) return;
      player.unmute();
      heard = true;
    });
  });

  const running = $derived(
    slides.length > 1 &&
      !hovering &&
      !focused &&
      !backgrounded &&
      !reduced &&
      !thrifty &&
      !trailerPlaying &&
      onscreen
  );

  /*
   * One timer, re-armed on every landing. Reading `index` here is deliberate:
   * a slide gets its full turn from the moment it lands, however it arrived.
   */
  $effect(() => {
    if (!running) return;
    const from = index;
    const total = slides.length;
    const timer = setTimeout(() => {
      index = (from + 1) % total;
    }, intervalMs);
    return () => clearTimeout(timer);
  });

  /* ── A finger turns the page ────────────────────────────────────────────── */

  /** The travel that means it: enough to be meant, not enough to be a wobble. */
  const SWIPE_MIN_PX = 40;
  /**
   * ...and not markedly MORE vertical than horizontal. This was 1.5 — a swipe
   * had to be half again more horizontal than vertical — and the owner's QA
   * (2026-08-17) called it out: "it has to be a perfect swipe". The browser
   * already takes clearly-vertical gestures as scrolls (`touch-action: pan-y`
   * fires pointercancel), so a gesture that reaches pointerup has ALREADY
   * survived pan detection; demanding dominance on top of that rejected honest
   * diagonal swipes. At 0.75, 80px across with 60px of wander turns the page;
   * 20px across with 90px down never gets here at all.
   */
  const SWIPE_RATIO = 0.75;
  /** How long the direction acknowledgement runs — the structure clock. */
  const SWIPE_NUDGE_MS = 240;

  /**
   * The one finger being tracked. Plain variables, not runes: they are written
   * and read inside event handlers only, and nothing in the template watches
   * them. A second finger while the first is down starts nothing.
   */
  let swipeId: number | null = null;
  let swipeX = 0;
  let swipeY = 0;
  /** True between a landed swipe and the synthetic click the browser echoes. */
  let swipeSwallows = false;
  let swallowTimer: ReturnType<typeof setTimeout> | undefined;
  /** Which way the last swipe went, for as long as its acknowledgement runs. */
  let swiped = $state<0 | 1 | -1>(0);
  let swipedTimer: ReturnType<typeof setTimeout> | undefined;

  function swipeStart(e: PointerEvent): void {
    // A fresh press owns its own click, whatever the last gesture was.
    swipeSwallows = false;
    // A mouse drags links and selects text; only a finger or a pen swipes.
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
    if (swipeId !== null) return;
    swipeId = e.pointerId;
    swipeX = e.clientX;
    swipeY = e.clientY;
  }

  /*
   * THE VERDICT IS GIVEN AT POINTERUP, nowhere earlier: a gesture is judged by
   * where it ended up, and a touch pointer is implicitly captured by the element
   * it pressed, so the up arrives here however far the finger wandered. A
   * mostly-vertical drag never reaches this handler at all — `touch-action:
   * pan-y` hands it to the browser as a scroll, which fires pointercancel.
   */
  function swipeEnd(e: PointerEvent): void {
    if (e.pointerId !== swipeId) return;
    swipeId = null;
    const dx = e.clientX - swipeX;
    const dy = e.clientY - swipeY;
    if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(dy) * SWIPE_RATIO) return;
    /*
     * A swipe is a swipe even when there is nowhere to go: the swallow is set
     * BEFORE the single-slide bail, because the one thing a swipe must never do
     * is fall through to "open the title". The flag is cleared by the click it
     * exists for, by the next press, or by the timer — a browser that declines
     * to synthesise a click after a drag must not leave a keyboard's Enter to
     * be eaten a minute later.
     */
    swipeSwallows = true;
    clearTimeout(swallowTimer);
    swallowTimer = setTimeout(() => (swipeSwallows = false), 500);
    if (slides.length < 2) return;
    const delta: 1 | -1 = dx < 0 ? 1 : -1;
    slideBy(delta);
    /*
     * `slideBy` is the whole state change; the rest is acknowledgement. The
     * rotation clock needs no help here — the interval effect reads `index`
     * and the dwell keys on `subject`, so the landing slide gets its full
     * still-then-trailer turn simply because the index moved.
     */
    swiped = delta;
    clearTimeout(swipedTimer);
    swipedTimer = setTimeout(() => (swiped = 0), SWIPE_NUDGE_MS + 60);
  }

  /** The browser took the gesture for a pan: no swipe, and no swallowed click. */
  function swipeCancel(e: PointerEvent): void {
    if (e.pointerId === swipeId) swipeId = null;
  }

  /*
   * Capture-phase, on the banner: the echo click lands on the full-bleed link
   * below, and this catches it on the way down. A plain tap never sets the
   * flag, so the link keeps its click and the banner stays a button.
   */
  function swallowSwipeClick(e: MouseEvent): void {
    if (!swipeSwallows) return;
    swipeSwallows = false;
    clearTimeout(swallowTimer);
    e.preventDefault();
    e.stopPropagation();
  }

  /* ── The dots — position, and the trailer's own progress (header note) ──── */

  /**
   * A dot press is the swipe's teardown-and-move pointed at a chosen slide:
   * stop the trailer, move the index, and the machinery already in place —
   * the dwell keyed on `subject`, the interval reading `index` — hands the
   * landing slide its fresh still-then-trailer clock, exactly as `slideBy`.
   */
  function jumpTo(at: number): void {
    if (at === index || at < 0 || at >= slides.length) return;
    stopTrailer();
    index = at;
  }

  /**
   * The active pill's fill, 0..1. Written by the poll below at 4Hz and drawn
   * as an inline scaleX — never a CSS-transition end state, so it keeps
   * moving on TV where transition-duration is snapped to 0.01ms.
   */
  let progress = $state(0);

  /*
   * THE POLL. 4Hz while a trailer holds the frame, nothing at all otherwise —
   * `time()` is a read of the API's own cached clock, not a layout or a
   * network touch, so this is four property reads a second at worst. The
   * formula is the header's: max of the two clocks that can turn the page.
   * `stageAt` and `player` are read inside the callback (untracked), so the
   * poll is keyed on `trailerPlaying` alone and survives the player handle
   * attaching mid-play without a rebuild.
   */
  $effect(() => {
    if (!trailerPlaying) {
      progress = 0;
      return;
    }
    const timer = setInterval(() => {
      const capFrac = (Date.now() - stageAt) / HERO_TRAILER_MAX_MS;
      let endFrac = 0;
      const p = player;
      if (p !== null) {
        const d = p.duration();
        if (d > 0) endFrac = p.time() / d;
      }
      progress = Math.min(1, Math.max(capFrac, endFrac));
    }, 250);
    return () => clearInterval(timer);
  });

  /* ── Arming: this one now, the next one shortly ─────────────────────────── */

  function arm(at: number): void {
    if (at < 0 || at >= slides.length || armedSeen.has(at)) return;
    armedSeen.add(at);
    const next: boolean[] = [];
    for (const i of armedSeen) next[i] = true;
    armed = next;
  }

  function armNext(): void {
    if (slides.length < 2 || !onscreen) return;
    arm((index + 1) % slides.length);
  }

  $effect(() => {
    const at = index;
    arm(at);
    /*
     * Only fetch ahead if there is going to BE a next slide of its own accord.
     * Off screen, under reduced motion, or on a metered connection there never
     * is, and the preload was measured at 724 KB for a picture nobody would see.
     * `hovering` is not in this list on purpose: it is a thing a person does
     * for a moment and then stops doing.
     */
    if (!onscreen || reduced || thrifty) return;
    const timer = setTimeout(armNext, PRELOAD_AFTER_MS);
    return () => clearTimeout(timer);
  });

  /* ── Derived caption ────────────────────────────────────────────────────── */

  /** The first four-digit year. "2008–2013" is honest, but the banner says 2008. */
  function yearOf(item: CatalogItem): string {
    const raw = item.year ?? item.releaseInfo ?? '';
    return /\d{4}/.exec(raw)?.[0] ?? '';
  }
  const year = $derived(current ? yearOf(current) : '');

  /** The age chip — only when the Wikidata table actually answers for this title. */
  const cert = $derived(current ? certOf(current.id) : null);

  function backdrop(item: CatalogItem): string {
    return item.background ?? backgroundUrl(item.id);
  }

  function fail(list: boolean[], at: number): boolean[] {
    const next = [...list];
    next[at] = true;
    return next;
  }

  /* The row above paints THIS picture as its ground. See top-ground.svelte.ts. */
  $effect(() => {
    const src = current ? backdrop(current) : null;
    return claimTopGround(src);
  });
</script>

<section
  class="hero"
  bind:this={node}
  style:--overscan={overscan.toFixed(4)}
  use:focusable={{ id: uid }}
  aria-label={current ? `Featured: ${current.name}` : 'Featured'}
  onpointerenter={() => (hovering = true)}
  onpointerleave={() => (hovering = false)}
  onpointerdown={swipeStart}
  onpointerup={swipeEnd}
  onpointercancel={swipeCancel}
  onclickcapture={swallowSwipeClick}
  onfocusin={() => (focused = true)}
  onfocusout={() => (focused = false)}
>
  <!-- `playing` rides the same reveal verdict as the stage's cross-fade: the
       moment video is uncovered, the sharp backdrop steps down so no static
       artwork sits behind a moving picture (the ambient blur beneath it is
       the surround until — and unless — the echo lands). -->
  <div class="art" class:playing={uncovered}>
    {#each slides as slide, i (slide.id)}
      <div
        class="layer"
        class:on={i === index}
        class:swipe-next={swiped === 1}
        class:swipe-prev={swiped === -1}
        aria-hidden="true"
      >
        {#if armed[i] && !artFailed[i]}
          <!--
            THE AMBIENT FILL. Same src as the sharp copy below it, so it is one
            request and one decode — the browser serves the second from cache.
            Blown up and blurred past all detail, it continues the picture's
            COLOUR to the edges of any screen, so a 2560px window does not get
            two dead columns and the backdrop does not have to be upscaled into
            mush to avoid them.
          -->
          <img class="amb" src={backdrop(slide)} alt="" aria-hidden="true" decoding="async" />
          <img
            class="sharp"
            src={backdrop(slide)}
            alt=""
            decoding="async"
            fetchpriority={i === 0 ? 'high' : 'auto'}
            onerror={() => (artFailed = fail(artFailed, i))}
          />
        {/if}
      </div>
    {/each}
    {#if trailerPlaying && trailerId && current}
      <!-- The banner behind the stage steps back — see `.dim`. -->
      <span class="dim" aria-hidden="true"></span>
    {/if}
    {#if echoWanted}
      <!--
        THE ECHO — the surround as a continuation of the trailer (header note).
        Under the scrim and the stage, over the backdrop; visible only once the
        stage's own reveal verdict has fired AND this frame has real motion in
        it, so the swap from blurred still to blurred video happens on the one
        clock and a dead echo simply never appears. Blurred and dimmed from its
        first frame, so YouTube's chrome is never legible here.
      -->
      <div class="echo" class:live={uncovered && echoLive} aria-hidden="true">
        <div class="echo-hole" bind:this={echoHole}></div>
      </div>
    {/if}
    {#if trailerPlaying && trailerId && current}
      <!--
        THE STAGE — the banner in the shape the video will be.

        16:9, because that is the only aspect ratio a YouTube trailer can
        actually be delivered in (docs/TRAILERS.md § E4) and cropping it into
        the banner's own shape threw half the picture away on a phone: at
        390x456 a cover-crop of a 16:9 frame loses 52% of its width, which is
        the same complaint the owner made about trailers in poster tiles.

        It mounts INVISIBLE and holds NO still of its own (`noStill` — the
        header's no-contained-thumbnail-beat note): the cover-filled backdrop
        behind it IS the still phase, unchanged while the embed loads, and
        the stage fades in only on the reveal verdict (`.art.playing`), the
        dim with it. Nothing moves when it opens and nothing moves when it
        goes: the banner's height never changes, the stage is a layer over
        the backdrop, and the backdrop dims behind it at the same moment so
        the bright rectangle reads as a screen rather than a seam. The
        overscan transform (header note) plays it a little larger than the
        box — top and bottom cropped by the frame's clip, sides never.

        UNDER THE LOW SCRIM now, deliberately: the caption is an overlay on
        the frame since the one-frame redesign, so the scrim after this block
        is what keeps the type legible where it crosses bright footage. The
        scrim is low and thin — the picture is uncovered, not dimmed — and it
        thins further while video shows (`.art.playing .scrim`), because the
        caption lightens on the same clock. YouTube's own branding never
        enters into it: the Trailer's oversize/bar-crop machinery keeps the
        chrome outside its box, whatever this banner lays on top.

        `tappable` is deliberately ABSENT now that the banner is a link: a tap
        here means "open the title page", and a device that refuses to autoplay
        keeps the finished-looking still until the silence timer moves the
        slideshow along.
      -->
      <div class="stage">
        <Trailer
          ytId={trailerId}
          {alternates}
          noStill
          label="{current.name} trailer"
          onplayer={(handle) => (player = handle)}
          onstate={noteState}
          onended={trailerFinished}
        />
      </div>
    {/if}
    <!-- Above the stage since the one-frame redesign: the caption sits ON the
         picture, so the low gradient has to as well. -->
    <span class="scrim" aria-hidden="true"></span>
  </div>

  {#if current}
    <!--
      THE BANNER IS THE BUTTON. Every explicit control was removed by the
      owner's brief, so this one full-bleed link is how the banner goes
      anywhere. It sits ABOVE the artwork and BELOW the caption in the
      stacking order; the caption takes no pointer events, so a press lands
      here wherever it falls. The visually hidden text is the accessible name
      twice over — content for the compiler's a11y pass, the label for the
      tree.
    -->
    <a class="open" href={titleHref(current.kind, current.id)} aria-label="Open {current.name}">
      <span class="sr">Open {current.name}</span>
    </a>
  {/if}

  <!--
    THE CAPTION IS AN OVERLAY ON THE FRAME — bottom-left, absolutely
    positioned, nothing of it in the page's flow. `quiet` rides the same
    reveal verdict as every other layer at the swap: while decoded video is
    on screen the caption drops to its light register (opacity only — cheap
    everywhere, snapped to instant on TV like all transitions there) and
    returns to full presence the moment a still does.
  -->
  <div class="body" class:quiet={uncovered}>
    {#if current}
      {@const shown = current}
      <div class="title">
        {#if shown.logo && !logoFailed[index]}
          <img
            class="logo"
            src={shown.logo}
            alt={shown.name}
            decoding="async"
            onerror={() => (logoFailed = fail(logoFailed, index))}
          />
        {:else}
          <h1>{shown.name}</h1>
        {/if}
      </div>

      <!-- Rating, the age chip when one exists, and year: see the certification note above. -->
      <p class="meta">
        {#if shown.imdbRating}
          <span class="rating"><Icon name="star" size={13} /> {shown.imdbRating}</span>
        {/if}
        {#if cert}
          <CertChip {cert} />
        {/if}
        {#if year}
          <span class="year">{year}</span>
        {/if}
      </p>

      <p class="blurb">{shown.description ?? ''}</p>
    {:else}
      <div class="title"><Skeleton width="62%" height="2.4rem" radius="10px" /></div>
      <p class="meta"><Skeleton width="14rem" height="0.8rem" radius="5px" /></p>
      <p class="blurb"><Skeleton width="80%" height="0.8rem" radius="5px" /></p>
    {/if}
  </div>

  {#if showVolume}
    <!--
      A trailer that started by itself started SILENT, because muted is the
      only autoplay any phone allows. This is the way back to sound — the
      shared MuteButton, so the banner and the tiles cannot drift apart. It is
      an overlay pinned to the frame's bottom-right corner, drawn only
      once the still has been uncovered (never over a still), and it takes no
      layout space, so nothing shifts when it comes or goes. Its own click
      handler stops propagation, so asking for sound never doubles as "open
      the title".
    -->
    <!-- `data-dpad-skip`, same as the tiles' volume circle: the banner is ONE
         stop on a remote. Without it, Up from the rail's rightmost tiles landed
         on this circle instead of the banner, and Up again wandered into the
         avatar — a tour of overlays nobody asked for. Sound stays a pointer
         affordance; a remote's own volume keys are the television's. -->
    <div class="volume" data-dpad-skip>
      <MuteButton muted={!heard} ontoggle={askForSound} size={40} />
    </div>
  {/if}

  {#if slides.length > 1}
    <!--
      THE DOTS (header note). Centred on the frame's last pixels, where the
      melt has already handed the ground to the page and the scrim is at its
      darkest — that geometry IS their scrim. The row itself takes no pointer
      events (the full-bleed link keeps every press that is not on a dot);
      each dot stops propagation like the MuteButton, because a jump must
      never double as "open the title". `data-dpad-skip` keeps the row out of
      the remote's map — the banner stays ONE stop — and on TV the buttons
      drop out of the tab order too: informational there, pressable everywhere
      else. Only the active dot carries the fill; the fill's scaleX is written
      by the 4Hz poll, so it moves even where transitions are snapped.
    -->
    <div class="dots" role="group" aria-label="Slideshow position" data-dpad-skip>
      {#each slides as slide, i (slide.id)}
        <button
          class="dot"
          class:here={i === index}
          tabindex={tvOn ? -1 : undefined}
          aria-label="Go to slide {i + 1} of {slides.length}: {slide.name}"
          aria-current={i === index ? 'true' : undefined}
          onclick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            jumpTo(i);
          }}
        >
          <span class="pip" aria-hidden="true">
            {#if i === index}
              <span class="fill" style:transform="scaleX({progress})"></span>
            {/if}
          </span>
        </button>
      {/each}
    </div>
  {/if}
</section>

<style>
  .hero {
    position: relative;
    display: block;
    /*
     * THE ONE FRAME'S HEIGHT — the only number the hero's size comes from
     * (owner QA 2026-08-17, twice: first "I want the hero smaller … that
     * should be the ENTIRE hero, including the graphical text", then — on
     * the shipped 56vh frame — "make the hero banner slightly larger"; the
     * header tells the whole story). ~62vh, clamped so a toy window keeps a
     * real banner (420px floor) and a tall 4K portrait window does not get
     * a wall; the ceiling rose from 640px WITH the vh, because at 700px a
     * 1080p screen actually feels the bump (669px of hero there) instead of
     * hitting the old cap. Measured at 1440×900: 558px of hero, the first
     * rail's heading still clearly above the fold. Viewport arithmetic,
     * never content: the rails below cannot jump, whatever a slide's
     * caption holds.
     */
    --hero-h: clamp(420px, 62vh, 700px);
    /*
     * THE CAPTION'S IDENTITY ROWS, as tokens: the wordmark box (`.title`
     * reserves exactly this) and one meta line (`.meta`'s reserved minimum).
     * Reserved so the OVERLAY never reflows between slides — a late logo or
     * a missing blurb moves nothing. Change a reserved height, change it
     * here — they are the same number twice on purpose.
     */
    --title-box: clamp(74px, 12vw, 140px);
    --meta-line: calc(var(--leading) * var(--text-sm));
    /*
     * THE STAGE: the largest uncropped 16:9 rect that fits the frame —
     * width-capped on narrow windows, height-capped on wide ones — centred
     * on both axes (`--stage-top` splits the vertical slack; the stage's own
     * left: 50% splits the horizontal). Whatever it does not cover is the
     * echo's surround: the sides on a wide short hero, the top-and-bottom
     * slack on a narrow tall one.
     */
    --stage-h: min(calc(100vw * 9 / 16), var(--hero-h));
    --stage-w: calc(var(--stage-h) * 16 / 9);
    --stage-top: calc((var(--hero-h) - var(--stage-h)) / 2);
    /*
     * THE MELT (header note): how much of the hero's lowest edge dissolves
     * into the page ground. The stage reaches the frame's bottom now, so the
     * melt eats the picture's own lowest pixels — deliberate, it is the only
     * seam the one-frame hero has — and it is shallower than the band-era
     * value so it reads as a dissolve, not a missing slice of video.
     */
    --hero-melt: clamp(56px, 9vh, 96px);
    /* Runs under the floating chrome — the pills on a desktop, the logo row and the
       bubble row on a phone. `--chrome-float`, never `--chrome-overlap`: the phone's
       top is not pinned, but the artwork still belongs behind it. See app.css. */
    margin-top: calc(var(--chrome-float) * -1);
    /* The frame IS the hero: height from the one token, no caption band below,
       and a breath before the first rail so its heading never sits on the melt. */
    height: var(--hero-h);
    margin-bottom: var(--space-4);
    overflow: clip;
    /* Vertical panning stays the browser's — a thumb scrolling the page over
       the banner must never be argued with. Only sideways travel is ours to
       read (the swipe). `pinch-zoom` is deliberately ABSENT: the owner banned
       page zooming app-wide (accidental zooms ruin the experience), and the
       banner must not re-allow what the page forbids. */
    touch-action: pan-y;
  }

  .art {
    position: absolute;
    inset: 0;
    background: radial-gradient(120% 90% at 70% 10%, var(--surface-2), var(--bg) 70%);
    /* The stage's maths moved to the `--stage-h` token, so no cq units are read
       here any more — but this stays, deliberately: it makes `.art` a
       stacking context (containment), which is why the volume overlay lives at
       banner level: nothing inside `.art` can be lifted above the link. Safe
       on an absolutely positioned layer: its size comes from the banner, never
       from its content. */
    container-type: size;
    /*
     * THE MELT. One mask over the WHOLE stack — backdrop, dim, echo, scrim —
     * so whatever the hero is showing, still or trailer, dissolves into the
     * page's own ground instead of butting against it. This is what removed
     * the bottom seam (the scrim used to fade to flat --bg, which does not
     * match the page's --app-ground wash). A mask is a compositor multiply,
     * no filter: the TV tier affords it.
     */
    -webkit-mask-image: linear-gradient(
      to bottom,
      #000 calc(100% - var(--hero-melt)),
      transparent 100%
    );
    mask-image: linear-gradient(to bottom, #000 calc(100% - var(--hero-melt)), transparent 100%);
  }
  /* No static backdrop behind a playing trailer, ever: the sharp copy steps
     down on the reveal clock and the ambient blur beneath it becomes the
     surround (until the echo covers it — and instead of it, on any failure). */
  .art.playing .layer .sharp {
    opacity: 0;
  }
  /*
   * Every slide is a layer in the same box. Nothing here can change the height
   * of anything: the picture arriving is an opacity change and nothing else.
   */
  .layer {
    position: absolute;
    inset: 0;
    opacity: 0;
    transition: opacity 900ms var(--ease-in-out);
  }
  .layer.on {
    opacity: 1;
  }
  /*
   * THE SWIPE'S ACKNOWLEDGEMENT. The incoming slide steps in from the side the
   * finger sent it — 24px on the structure clock, transform only, riding the
   * same crossfade the automatic advance uses. Only a manual swipe wears these
   * classes (and only for `SWIPE_NUDGE_MS`); the timer's own advance stays a
   * plain dissolve.
   */
  .layer.on.swipe-next {
    animation: swipe-in-next 240ms var(--ease-out);
  }
  .layer.on.swipe-prev {
    animation: swipe-in-prev 240ms var(--ease-out);
  }
  @keyframes swipe-in-next {
    from {
      transform: translateX(24px);
    }
  }
  @keyframes swipe-in-prev {
    from {
      transform: translateX(-24px);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .layer.on.swipe-next,
    .layer.on.swipe-prev {
      animation: none;
    }
  }
  .layer .amb {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 22%;
    /* Past all detail on purpose: this is a colour field, not a picture. */
    filter: blur(56px) saturate(1.3) brightness(0.62);
    transform: scale(1.12);
  }
  /*
   * The sharp copy keeps the old cap, and that cap now earns its keep: it is
   * what stops a 1920-wide backdrop being upscaled across a 2560 screen. Past
   * the cap the ambient layer takes over and the picture simply carries on.
   */
  .layer .sharp {
    position: relative;
    display: block;
    width: 100%;
    max-width: var(--page-max);
    height: 100%;
    margin-inline: auto;
    object-fit: cover;
    object-position: center 22%;
    /* The step-down above rides this; the slide crossfade is `.layer`'s own. */
    transition: opacity 700ms var(--ease);
    /* Feather the seam so the sharp edge does not draw a vertical line. */
    -webkit-mask-image: linear-gradient(to right, transparent, #000 5%, #000 95%, transparent);
    mask-image: linear-gradient(to right, transparent, #000 5%, #000 95%, transparent);
  }
  /* No seam to hide when the picture already fills the window. */
  @media (max-width: 1520px) {
    .layer .sharp {
      -webkit-mask-image: none;
      mask-image: none;
    }
  }
  /*
   * THE STAGE. A 16:9 box laid out as the largest fit, then played a touch
   * LARGER than that fit (the overscan — header note): the box is contained,
   * the transform oversizes it, the frame's clip crops only top and bottom,
   * and the sides never leave the frame. In ONE place on every screen:
   * CENTRED in the frame, both axes — `--stage-top` is zero exactly when the
   * stage fills the frame's height (any wide window, where the height is the
   * cap) and shares the slack when a narrow one leaves some, so "top-pinned"
   * cannot happen by fiat, only where geometry leaves nothing to centre.
   * Whatever the scaled stage still does not cover is the surround the echo
   * plays in — the sides on a desktop, above and below on a phone (where the
   * overscan is exactly 1). The caption overlays the frame's bottom-left;
   * where it crosses the picture it is in its quiet register, which is the
   * deal the owner asked for.
   */
  .stage {
    position: absolute;
    top: var(--stage-top);
    left: 50%;
    /*
     * Centred, then OVERSCANNED (header note): `--overscan` is
     * min(1.15, heroWidth / stageWidth), written by the script from the
     * frame's measured box. `translateX(-50%)` runs after the scale in the
     * mapping, and the scale is about the element's own centre, so the
     * scaled box stays centred on both axes and its overflow is split
     * equally — the top and bottom halves fall to the frame's
     * `overflow: clip`, the sides by construction never reach it. A
     * transform, not a bigger box: YouTube keeps picking its quality rung
     * from the layout size (zero extra bytes), and the TV tier affords a
     * compositor-only scale. The bar-crop inside composes safely — it zooms
     * within this box's clip and can never widen it.
     */
    transform: translateX(-50%) scale(var(--overscan, 1));
    width: min(var(--stage-w), 100%);
    height: var(--stage-h);
    overflow: hidden;
    /* A FEATHERED JOIN, not a butt joint. On a wide screen the stage is ~930 px
       of a 1440 px banner, and a flat dim beside it drew a hard vertical seam
       down the middle of the picture. This spills darkness outwards and fades,
       so the screen looks lit rather than cut out. */
    box-shadow: 0 0 90px 28px rgb(3 5 9 / 0.72);
    /*
     * INVISIBLE UNTIL THE REVEAL — the no-contained-thumbnail rule (header).
     * The stage used to fade in at mount, which put its contained still over
     * the cover backdrop for the whole pre-reveal beat. Now it mounts at
     * opacity 0 (the embed loads and plays unseen; opacity does not gate
     * autoplay) and `.art.playing` — the same `uncovered` verdict every
     * other layer rides — fades it in over the same 700ms the sharp
     * backdrop steps down: one straight cross-fade, backdrop to motion.
     */
    opacity: 0;
    transition: opacity 700ms var(--ease);
  }

  /* The banner behind the stage steps back, so a 16:9 picture inside a wider
     box reads as a screen rather than as a mistake. On the reveal clock like
     the stage: a dim arriving before the video is a visible beat of its own,
     which is exactly what the owner asked to lose. */
  .dim {
    position: absolute;
    inset: 0;
    background: rgb(3 5 9 / 0.38);
    pointer-events: none;
    opacity: 0;
    transition: opacity 700ms var(--ease);
  }

  .art.playing .stage,
  .art.playing .dim {
    opacity: 1;
  }

  /*
   * THE ECHO. The iframe is laid out at a fixed 384x216 — that is what keeps
   * YouTube on the cheap quality rung and the blur on a small surface (the
   * measured story is on the frame-creation code in the script) — and a
   * single transform, set there, scales the already-blurred picture up until
   * it covers the hero. Blur-before-scale means the effective softness is
   * 16px x scale ≈ 56-120px at real hero sizes, comfortably past the 48px
   * where YouTube's chrome stops being legible and sync drift stops being
   * visible. It fades in on `.live` — the stage's reveal verdict AND its own
   * PLAYING — and until then it simply is not there, which leaves the blurred
   * backdrop still as the surround. Mounted on EVERY surface (phone, tablet,
   * desktop, TV — the owner's one-seamless-surface rule); only reduced
   * motion and Save-Data keep the still surround, and those refuse the
   * trailer itself. On TV the same filter was measured at 6× and 8× CPU
   * throttle before being kept — the numbers live in the verification notes
   * of the branch that shipped this; the cost stays flat because the filter
   * rasterises the 384×216 layout box, never the screen.
   */
  .echo {
    position: absolute;
    inset: 0;
    overflow: hidden;
    opacity: 0;
    transition: opacity 700ms var(--ease);
    pointer-events: none;
    contain: strict;
  }
  .echo.live {
    opacity: 1;
  }
  .echo-hole {
    position: absolute;
    inset: 0;
  }
  .echo-hole :global(iframe) {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 384px;
    height: 216px;
    border: 0;
    pointer-events: none;
    /* LUMINOUS, not a haze — the owner's second pass (header note): 0.55
       read as a dark surround and the brightness leg rose to 0.8. The
       legibility jobs were never this number's: YouTube's chrome is
       destroyed by blur × scale, and the caption's floor is the scrim and
       the melt, which still own the bottom of the frame. The transform
       (translate + scale) is inline, computed from the hero's real size at
       creation. */
    filter: blur(16px) saturate(1.35) brightness(0.8);
  }
  /*
   * THE TV TREATMENT — presence identical, cost engineered (the measured
   * story is on `ECHO_OVERSIZE` in the script): no filter of any kind, the
   * ~5× bilinear stretch of a 216p frame is the softness, this quad is the
   * dimming, and the 1.5 oversize keeps YouTube's chrome outside the hero.
   */
  :global(html[data-tv='on']) .echo-hole :global(iframe) {
    filter: none;
  }
  :global(html[data-tv='on']) .echo::after {
    content: '';
    position: absolute;
    inset: 0;
    /* Thinned 0.52 → 0.32 with the filter tier's brightness lift (header
       note): the same "brighter, not a haze" on the surface that dims by
       compositing instead of filtering. */
    background: rgb(3 5 9 / 0.32);
  }

  @media (prefers-reduced-motion: reduce) {
    .stage,
    .dim,
    .echo,
    .layer .sharp,
    .title,
    .meta,
    .blurb,
    .scrim,
    .pip,
    .fill {
      transition: none;
    }
  }

  /*
   * THE LOW SCRIM. A GRADIENT IS NOT A SURFACE: `aria-hidden`, decorative,
   * no pointer events — everything pressable sits ABOVE it. Since the
   * one-frame redesign it is the LAST layer in `.art`, over the stage,
   * because the caption crosses the picture now and the type needs a floor
   * where the footage is bright. It is deliberately LOW — real darkness only
   * in the caption's own corner (bottom, weighted left), transparent by
   * halfway up the frame — so the picture reads uncovered, never dimmed.
   */
  .scrim {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(
        to top,
        color-mix(in oklab, var(--bg) 88%, transparent) 0%,
        color-mix(in oklab, var(--bg) 50%, transparent) 24%,
        transparent 54%
      ),
      linear-gradient(to right, color-mix(in oklab, var(--bg) 36%, transparent), transparent 44%);
    transition: opacity 700ms var(--ease);
  }
  /*
   * While the trailer plays the caption has stepped down to its quiet
   * register, so the scrim steps down with it — same 700ms, same verdict —
   * and the moving picture keeps nearly all of its light. The echo is
   * already dimmed (brightness 0.55; the quad on TV) and the melt still
   * closes the bottom, so nothing beneath the type ever glares.
   */
  .art.playing .scrim {
    opacity: 0.55;
  }

  /*
   * The full-bleed link. Above the artwork, below the caption; the caption is
   * `pointer-events: none`, so wherever a press lands it lands here. The focus
   * ring is drawn INSET because the element meets the viewport edges — an
   * outset ring would simply be clipped away by `overflow: clip`.
   */
  .open {
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: var(--radius-sm);
  }
  .open:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: -4px;
  }
  /*
   * NO RING ON TV — the owner's call (2026-08-17): "a highlighted focus box
   * the same way the tiles have … quite ugly and cheapens it." An earlier
   * pass drew an overscan-inset ::after border here; it is gone entirely, and
   * nothing replaces it. The hero being the ONLY ringless focusable IS the
   * signal: when no tile below wears a ring, the remote is on the banner —
   * and any subtler cue would need a transition the TV tier snaps to
   * nothing, or a filter it forbids. Desktop keyboard focus keeps its
   * `:focus-visible` outline above: that one is accessibility, not chrome.
   */
  :global(html[data-tv='on']) .open:focus-visible {
    outline: none;
  }
  .open .sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .body {
    /*
     * THE OVERLAY. Bottom-left of the frame, absolutely positioned: the
     * caption takes no layout space, so nothing below the hero can move on
     * its account. `left` + `right` + `max-width` anchor it left at a
     * readable measure; the right padding is the volume circle's corner (40px
     * button + its gap), which the caption must never run under.
     */
    position: absolute;
    z-index: 2;
    left: max(var(--page-pad), var(--safe-l));
    right: max(var(--page-pad), var(--safe-r));
    bottom: var(--space-4);
    display: grid;
    justify-items: start;
    gap: var(--space-2);
    max-width: 52ch;
    padding-right: calc(40px + var(--space-3));
    /* The caption is words, not controls; presses fall through to the link. */
    pointer-events: none;
  }
  /*
   * THE QUIET REGISTER (header note). While decoded video is on screen the
   * caption steps down — wordmark to 0.62, meta and blurb lighter still —
   * and steps back the moment a still returns. Opacity ONLY: it is the one
   * property the TV tier's transition snap and the compositor both make
   * free, and a weight change would re-shape the very glyphs mid-fade. The
   * rows carry the transition (not `.body`) so each keeps its own paint.
   */
  .title,
  .meta,
  .blurb {
    transition: opacity 700ms var(--ease);
  }
  .body.quiet .title {
    opacity: 0.62;
  }
  .body.quiet .meta,
  .body.quiet .blurb {
    opacity: 0.42;
  }

  /* ONE BOX FOR THE NAME, whichever way it is drawn. A wordmark, a fallback
     heading and a loading plate are all exactly this tall — and the height is
     the `--title-box` token because the stage's caption allowance subtracts
     this exact number (see the geometry note on `.hero`). */
  .title {
    display: flex;
    align-items: flex-end;
    width: 100%;
    height: var(--title-box);
  }

  h1 {
    font-size: var(--text-hero);
    font-weight: var(--weight-bold);
    line-height: var(--leading-tight);
    letter-spacing: var(--tracking-tight);
    text-wrap: balance;
    /* Two lines is what the box holds. A third would spill over the meta line. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .logo {
    max-height: 100%;
    max-width: min(100%, 400px);
    width: auto;
    object-fit: contain;
    object-position: left bottom;
    filter: drop-shadow(0 4px 18px rgb(0 0 0 / 0.55));
  }

  /* ONE LINE, ALWAYS — its height is reserved even while the skeleton shows,
     so the first title landing cannot move the blurb. */
  .meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    max-width: 100%;
    /* Stated rather than inherited from whatever is in the row: with a 13px
       star in it the line box is 20px and without one it is 17.5px, and that
       2.5px was a measured shift when the first title landed. The reserved
       minimum is the `--meta-line` token because the stage's caption
       allowance subtracts this exact number (geometry note on `.hero`). */
    line-height: var(--leading);
    min-height: var(--meta-line);
    color: var(--text-dim);
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
  }
  .rating {
    display: inline-flex;
    flex: none;
    align-items: center;
    gap: 4px;
    color: var(--warn);
  }
  .year {
    flex: none;
  }

  /* THREE LINES OF SPACE WHETHER OR NOT THERE ARE THREE LINES OF WORDS. */
  .blurb {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    height: calc(3 * var(--leading) * var(--text-sm));
    color: var(--text-dim);
    font-size: var(--text-sm);
    line-height: var(--leading);
    text-wrap: pretty;
  }

  /*
   * The volume overlay rides the FRAME's bottom-right corner: the one-frame
   * hero has one corner, and the echo makes the whole frame the moving
   * picture, so the frame's corner and the picture's are the same statement.
   * It cannot live inside `.art` (see the stacking note there), so it is the
   * banner's own overlay. `--safe-r` (not env(): app.css owns that spelling)
   * keeps it off a notch, and on TV the token's 4vw floor keeps it inside
   * overscan; `--page-pad` lines it up with the caption's own margin.
   */
  .volume {
    position: absolute;
    z-index: 3;
    bottom: var(--space-3);
    right: max(var(--page-pad), var(--safe-r), var(--space-3));
  }

  /*
   * THE DOTS (header note). The row rides the frame's very bottom, INSIDE
   * the melt: by these pixels the artwork has dissolved to the page's own
   * dark ground and the low scrim is at its 88% floor, so the pips read over
   * the brightest artwork with no scrim of their own — geometry is the
   * scrim. The measured seat: the pip band sits ~8–13px above the frame's
   * edge, which clears the caption's reserved floor (text boxes end 12–16px
   * up, their glyphs' half-leading higher still) on both breakpoints, so
   * dots and blurb can never collide even where the caption spans the
   * centre. The row takes no pointer events; only the buttons do — the
   * full-bleed link keeps everything between them.
   */
  .dots {
    position: absolute;
    z-index: 3;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  /* The painted pip stays small. Coarse pointers get a 44px button below. */
  .dot {
    display: grid;
    place-items: center;
    margin: 0;
    padding: 8px 6px;
    border: 0;
    background: none;
    cursor: pointer;
    pointer-events: auto;
  }
  /*
   * The pip: neutral text-mixes ONLY — `--accent` belongs to the fill
   * (progress is pink in this app), never to an inactive dot, and selection
   * stays `--focus-ring`'s job. The 1px dark halo is the whole "scrim":
   * enough that a white-mix pip cannot vanish into a white sky in the still
   * phase's brightest corner. Width is the one thing that moves (the app's
   * structure easing); on TV it snaps with every other transition.
   */
  .pip {
    position: relative;
    overflow: hidden;
    width: 5px;
    height: 5px;
    border-radius: var(--radius-pill);
    background: color-mix(in oklab, var(--text) 40%, transparent);
    box-shadow: 0 0 0 1px rgb(0 0 0 / 0.3);
    transition:
      width 280ms var(--ease-out),
      background-color 280ms var(--ease-out);
  }
  .dot:hover .pip {
    background: color-mix(in oklab, var(--text) 65%, transparent);
  }
  .dot:focus-visible {
    outline: none;
  }
  .dot:focus-visible .pip {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
  .dot.here .pip {
    width: 30px;
    background: color-mix(in oklab, var(--text) 32%, transparent);
  }
  /*
   * The trailer's progress, drawn as a scaleX from the 4Hz poll: a transform
   * on a compositor layer, cheap everywhere. The 260ms linear transition
   * only smooths the quarter-second steps into one motion — on TV it snaps
   * to 0.01ms and the fill still advances, because the VALUE keeps arriving
   * from JS; under reduced motion the same, discretely, which that
   * preference permits (it is progress, not decoration).
   */
  .fill {
    position: absolute;
    inset: 0;
    background: var(--accent);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 260ms linear;
  }

  @media (max-width: 767px) {
    .hero {
      /* THE PHONE'S FRAME — SHORTER than the band era, which is the point:
         the caption overlays the picture now, so the strip it used to own
         comes back to the page. min(84vw, 50vh) is 328px at 390×844 (the
         band-era hero was ~388), still tall enough that the width-fit video
         (219px at 390) leaves the caption mostly over the echo's surround
         rather than the picture. The 16:9 fit and the centring are the same
         desktop tokens — nothing else changes shape. */
      /*
       * THE PICTURE AT THE TOP, THE WORDS BELOW IT. The owner, from his
       * phone, with two red rectangles: "move the video to the top - the top
       * of the video should be right below the top banner. That leaves more
       * room for the text." And: "the slideshow dots and the play time are
       * displayed within the description text. They should never be within
       * the text - underneath it."
       *
       * Both came from one thing: the 16:9 stage was CENTRED in a taller
       * frame (the desktop rule), so on a 390px phone there was a band of
       * nothing above the picture, and the caption and the dots - anchored
       * to the frame's bottom - had nowhere to sit but on top of the
       * picture's lower third and each other.
       *
       * So the phone frame is the picture plus a caption strip. The stage
       * pins to the top (`--stage-top: 0`), the caption sits in its own
       * strip beneath the picture rather than over it, and the dots go at
       * the very bottom, under the caption, in a row of their own.
       */
      --stage-h: calc(100vw * 9 / 16);
      --stage-top: 0px;
      /* Picture + the caption's strip. The strip reserves what the wordmark
         box, one meta line and a two-line blurb actually need, so a longer
         description cannot push the rails below. */
      --caption-strip: calc(var(--title-box) + var(--space-1) + 1.4em + var(--space-1) + 2 * var(--leading) * var(--text-sm) + var(--space-3) + 28px);
      --hero-h: calc(var(--stage-h) + var(--caption-strip));
      --title-box: clamp(58px, 10vw, 120px);
    }
    .body {
      /* Below the picture, not over it. Anchored to the frame's bottom above
         the dots' row - see `.dots` in this block. */
      gap: var(--space-1);
      bottom: calc(var(--space-3) + 28px);
      padding-right: max(var(--page-pad), var(--safe-r));
    }
    .blurb {
      -webkit-line-clamp: 2;
      line-clamp: 2;
      height: calc(2 * var(--leading) * var(--text-sm));
    }
    /* The dots' own row, under everything, exactly as tall as the space the
       caption leaves for them. Never inside the text again. */
    .dots {
      height: 28px;
      bottom: 0;
    }
    /* The sound button belongs to the picture, so on a phone it sits at the
       picture's bottom-right corner rather than the frame's. */
    .volume {
      top: calc(var(--stage-h) - 40px - var(--space-3));
      bottom: auto;
    }
  }
  @media (pointer: coarse) {
    .dot { min-width: var(--hit); min-height: var(--hit); }
  }
</style>
